import { ComparisonTable } from '@cxc/react-hydro-components';
import styles from './style.module.scss';
import { useAtom } from 'jotai';
import { aemContent } from '../state/globalState';
import React, { useEffect, useState } from 'react';
import { htmlReactParserWrapper } from '../utils/htmlReactParserWrapper';
import { constructDigitalData, fireAnalyticsEvent } from '../analytics/analytics';
import { ESWITCH_PAGE_SCROLL_ONCLICKS, GET_DETAILED_ASSESSMENT_BUTTON_ONCLICKS, GLOBAL_INTERACTION, PAGE_NAME } from '../analytics/analyticsConstants';
import { useUniversalContext } from '@cxc/react-universal-components';
import { AnalyticsConstants, URLConstants, ViewTypeConstants, marketConfiguration } from '../utils/constants';
import InfoTableColumnContent from './InfoTableColumnContent';

interface TableColumns {
    ariaLabel: string;
    buttonTarget: string;
    buttonTitle: string;
    buttonStyle: string;
    eventName: string;
    headingText: React.JSX.Element;
    subHeadingText: React.JSX.Element;
}

interface TableBody {
    text: string;
    value: [string];
}

const COMPARISON_TABLE = 'ComparisonTable';

export default function InfoTable() {
    const [aem] = useAtom(aemContent);
    const [tableColumns, setTableColumns] = useState<TableColumns | null>(null);
    const [tableBody, setTableBody] = useState<TableBody | null>(null);
    const { navigate, language, isLoggedIn, subscribeScopedValue } = useUniversalContext();
    const cookiePreferences = subscribeScopedValue(AnalyticsConstants.FPG_COOKIES, AnalyticsConstants.COOKIE_PREFERENCES);
    const getStartedEnabled = !marketConfiguration[language].enableGetStarted;
    const buttonsDisabled = [false, getStartedEnabled];

    useEffect(() => {
        initializeTableColumnsAndBody();
    }, []);

    const initializeTableColumnsAndBody = () => {
        if (aem) {
            initializeTableColumnsFromAem();
            initializeTableBodyFromAem();
        }
    };

    const initializeTableColumnsFromAem = () => {
        const aemColumns = aem?.['comparison-table']?.['props']?.['tableColumns'] || [];
        const columns: any = [];
        const buttonTypes = [ViewTypeConstants.QUICK_ESTIMATE, ViewTypeConstants.DETAILED_ASSESSMENT];
        for (let i = 0; i < aemColumns.length; i++) {
            columns.push({
                headingText: htmlReactParserWrapper(aemColumns[i].heading),
                buttonTitle: aemColumns[i]?.ctaText,
                buttonDisabled: buttonsDisabled[i],
                ariaLabel: aemColumns[i]?.ctaAriaLabel || '',
                subHeadingText: <InfoTableColumnContent aemColumnTextContent={aemColumns[i].subHeading?.replace('*', '1')} />,
                onClick: () => {
                    !buttonsDisabled[i] && handleTableButtonClick(buttonTypes[i], COMPARISON_TABLE);
                },
            });
        }
        setTableColumns(columns);
    };

    const initializeTableBodyFromAem = () => {
        const aemTableBody = aem?.['comparison-table']?.['props']?.['tableBody'] || [];
        const body: any = [];
        for (const aembody of aemTableBody) {
            const sanitzedText = aembody.text.replace(/{/g, '(').replace(/}/g, ')');
            body.push({ text: sanitzedText, values: aembody.values });
        }
        setTableBody(body);
    };

    const navigateToDetailedAssement = () => {
        navigate(URLConstants.DETAILED_ASSESSMENT);
    };

    const handleTableButtonClick = (buttonType: string, location: string) => {
        if (buttonType === ViewTypeConstants.QUICK_ESTIMATE) {
            const digitalData = constructDigitalData(buttonType, PAGE_NAME, GLOBAL_INTERACTION, '', '', ESWITCH_PAGE_SCROLL_ONCLICKS + buttonType, isLoggedIn, cookiePreferences);
            fireAnalyticsEvent(digitalData.digitalData, digitalData.directCallName);
            document.getElementById('quick-estimate-calculator')?.scrollIntoView({ behavior: 'smooth' });
        } else {
            const digitalData = constructDigitalData(buttonType, PAGE_NAME, GLOBAL_INTERACTION, '', '', GET_DETAILED_ASSESSMENT_BUTTON_ONCLICKS, isLoggedIn, cookiePreferences);
            fireAnalyticsEvent(digitalData.digitalData, digitalData.directCallName);
            navigateToDetailedAssement();
        }
    };

    return (
        <>
            {tableColumns && tableBody && (
                <div className="fds-layout-grid">
                    <div className={`fds-layout-grid__cell--span-12 ${styles.mrgTop54}`}>
                        <ComparisonTable tableColumns={tableColumns} tableBody={tableBody} />
                    </div>
                </div>
            )}
        </>
    );
}
